import { z } from 'zod';
import { isoDateTimeSchema, rawPhoneSchema, uuidSchema } from './common.js';
import { MAX_AMOUNT_CENTS } from './program-rules.js';
/**
 * Offline sync — API-CONTRACT.md §3 (v2), ARCHITECTURE.md §5.
 *
 * The counter writes locally first and posts a batch when it reconnects. Two
 * properties matter more than anything else here, and both are AGENTS.md rules
 * rather than preferences:
 *
 *  - **A replayed queue creates one event, not two** (§4.2). The client
 *    generates `idempotencyKey` *before* the first network attempt, so a batch
 *    posted twice — a lost response, a second device, a browser restart
 *    mid-flush — resolves to the same events.
 *  - **Partial failure is normal** (ARCHITECTURE.md §5). One rejected event must not discard a
 *    shift's worth of stamps, so every event is judged on its own and the
 *    response says what happened to each.
 */
/**
 * How far back an offline event may be dated.
 *
 * 72 hours covers a long weekend of outage, which is the case this exists for.
 * Beyond that the event is more likely a clock that was wrong or a queue that
 * should have been abandoned, and silently accepting it would put stamps on the
 * wrong side of a program version change or an expiry run.
 */
export const MAX_EVENT_AGE_HOURS = 72;
/**
 * How far into the future a client clock may run.
 *
 * A till whose clock is ahead would otherwise date stamps into tomorrow, which
 * moves them past a daily cap and out of today's rollup. Five minutes absorbs
 * ordinary drift without absorbing a wrong date.
 */
export const MAX_CLOCK_SKEW_MINUTES = 5;
/** Batch size. Large enough for a full shift, small enough to bound one request. */
export const MAX_SYNC_BATCH = 200;
/**
 * One queued event.
 *
 * Only EARN is queueable. Redemption is online and staff-confirmed without
 * exception (AGENTS.md §4.5) — granting a reward from a stale balance is how a
 * shop gives away product it never agreed to — and adjustments and voids are
 * owner actions taken at a dashboard that is online by definition.
 */
export const syncEventSchema = z.strictObject({
    /** Generated on the device before the first attempt. This is the dedupe key. */
    idempotencyKey: uuidSchema,
    brandId: uuidSchema,
    locationId: uuidSchema,
    /**
     * Absent when the customer was enrolled offline and has no server id yet —
     * `phoneE164` identifies them instead, and the server creates or finds them.
     */
    enrollmentId: uuidSchema.nullable().default(null),
    /**
     * Present for an offline enrollment. Normalised on the server like every
     * other boundary, and keyed on so two devices enrolling the same new customer
     * merge into one rather than duplicating (features/STAFF-COUNTER.md).
     */
    phoneE164: rawPhoneSchema.nullable().default(null),
    quantity: z.number().int().min(1).max(10).default(1),
    /** Bounded like the live path — see `loyaltyEarnSchema`. */
    amountCents: z.number().int().min(0).max(MAX_AMOUNT_CENTS).nullable().default(null),
    /**
     * The customer's rotating code, when the stamp began with a scan.
     *
     * Carried so single-use survives an outage. Without it a queued stamp wrote no
     * `qr_token_uses` row, and the same screenshot could be stamped from the queue
     * *and* again live — reopening the hole features/LOYALTY-ENGINE.md exists to close, on the
     * one path where nobody is watching.
     *
     * The token will usually be long expired by the time the queue drains, which
     * is why an expired code cannot reject a queued stamp — see `SyncService`.
     * What it can still do is stop the *same* code being spent twice.
     */
    qrToken: z.string().min(1).nullable().default(null),
    /** When it happened at the counter, by the device's clock. */
    occurredAt: isoDateTimeSchema,
});
export const syncBatchSchema = z.strictObject({
    events: z.array(syncEventSchema).min(1).max(MAX_SYNC_BATCH),
});
export const SYNC_RESULT_STATUSES = ['APPLIED', 'DUPLICATE', 'REJECTED', 'RETRY'];
/**
 * Refusals that will be refused identically forever.
 *
 * The client deletes a queued event only for one of these. Everything else —
 * `RETRY`, a transport failure, an unrecognised code — keeps the row, because
 * the alternative is deleting a stamp that would have landed on the next
 * attempt.
 *
 * The distinction is the whole reason `RETRY` exists. Collapsing every failure
 * into `REJECTED` meant a connection blip mid-batch, a write conflict, or a
 * failover produced a rejection the client then deleted — the stamp gone from
 * both sides, which is the one outcome the queue is built to prevent.
 *
 * `PROGRAM_INACTIVE` is deliberately *not* here: it is what an owner swapping a
 * program version looks like from a till, and it clears on its own.
 */
export const TERMINAL_SYNC_ERRORS = [
    'EVENT_TOO_OLD',
    /**
     * Permanent by construction: the same amount against the same rules gets the
     * same answer forever. Left off this list it produced a queue row that could
     * never drain and was retried on every flush.
     */
    'BELOW_MINIMUM_SPEND',
    'CLOCK_SKEW_REJECTED',
    'VALIDATION_FAILED',
    'NOT_FOUND',
    'BRAND_MISMATCH',
    'ENROLLMENT_ARCHIVED',
];
export function isTerminalSyncError(code) {
    return TERMINAL_SYNC_ERRORS.includes(code ?? '');
}
//# sourceMappingURL=sync.js.map